// Admin: Today (AdminToday.dc.html), at /admin: the day's date and "n of m things need you", Live
// today, Needs you, New this week, and beside them Announcements, Coming up, Going quiet and Access
// ending. Every section reads its own hub.today_* read (docs/data-model.md 10.4.2) as the staff
// member, so each role gets its own Today: owner and admin everything, support the questions,
// recordings to paste, registers and finishers, a host their product's. Products not moved into the
// hub, test logins, and comp and test access never show.
//
// Amounts come from stripe-prices' owner and admin part, by the ids the reads give; nothing stores an
// amount. A section that fails says so on its own and offers Try again; the rest still draw.
//
// At 1440 the 2 columns are the board's; under 1100 they stack, and under 760 every row stacks with
// the same buttons (Adam, 4 Oct, answer 5).
//
// For owner and admin, each mastermind moved into the hub adds its own jobs to Needs you (the Accelerator
// critique's P1, Adam's yes of 20 Oct): its reviews after a session, members still to set up, and a register
// job that stays until everyone's marked, worked out from Masterminds' own reads (mastermindJobs.js). Those
// reads go after the main jobs and are failure-tolerant: if 1 fails, Today draws as it did without them.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { PageHeader, useAdmin, useLoad } from '../ui/index.js'
import { NeedsYou, makeOps } from '../today/Jobs.jsx'
import { Announcements, readAnnouncements } from '../today/Announcements.jsx'
import { LiveToday, NewThisWeek, ComingUp, GoingQuiet, AccessEnding } from '../today/Sections.jsx'
import { amountIds, bellAsks, eyebrowFor, introductionIds, keepPlaces, leftLine, refundedLines } from '../today/words.js'
import { mastermindJobs, withMastermindJobs } from '../today/mastermindJobs.js'
import { loadMembers, loadGroups, loadSessions } from '../mastermind/data.js'
import { refusal } from '../referrals/words.js'
import '../../styles/admin-today.css'

export default function Today({ me, role, api }) {
  const { products } = useAdmin()
  // the page's moment: the reads' p_at, and what "today" means in its words
  const nowMs = useMemo(() => Date.now(), [])
  const at = useMemo(() => new Date(nowMs).toISOString(), [nowMs])
  const personId = me?.who?.person_id || null
  const canMoney = role === 'owner' || role === 'admin'

  const jobs = useLoad(() => api.today_jobs({ at }), [api, at])
  const schedule = useLoad(() => api.today_schedule({ at }), [api, at])
  const fresh = useLoad(() => api.today_new({ at }), [api, at])
  const quiet = useLoad(() => api.today_going_quiet({ at }), [api, at])
  const ending = useLoad(() => api.today_access_ending({ at }), [api, at])
  const news = useLoad(() => readAnnouncements(api), [api])

  // Each mastermind's members, groups and sessions (owner and admin), read once the main jobs are in, each
  // mastermind's 3 reads together; 1 that fails is left out. mm: mastermindJobs' answer, null until it's read.
  const [mm, setMm] = useState(null)
  const mmAsked = useRef(false)
  useEffect(() => {
    if (!canMoney || !jobs.data || mmAsked.current) return
    const masterminds = (products || []).filter(p => p.type === 'mastermind' && p.moved_in)
    if (!masterminds.length) return
    mmAsked.current = true
    Promise.all(masterminds.map(product => Promise.all([loadMembers(api, product.id), loadGroups(api, product.id), loadSessions(api, product.id)])
      .then(([members, groups, sessions]) => ({ product, members: members || [], groups: groups || [], sessions: sessions || [] }), () => null)))
      .then(reads => setMm(mastermindJobs(reads.filter(Boolean), { nowMs, role })))
  }, [api, canMoney, jobs.data, products, nowMs, role])

  // Needs you's rows keep their places across reloads, so a ticked job stays where it was, faded. A
  // tick still on its way keeps its new state over a read that left before it (pending, by job). Until
  // a job's been ticked or a button pressed, the masterminds' jobs arriving put the list in order again.
  const [rows, setRows] = useState(null)
  const pending = useRef(new Map())
  const touched = useRef(false)
  useEffect(() => {
    if (!jobs.data) return
    const ticked = pending.current
    const all = withMastermindJobs(jobs.data, mm)
    setRows(before => keepPlaces(touched.current ? before : null, all).map(j => (ticked.has(j.job_key) ? { ...j, ...ticked.get(j.job_key) } : j)))
  }, [jobs.data, mm])

  // The amounts the cards and New this week show, read once both have loaded (owner and admin only)
  const [amounts, setAmounts] = useState(null)
  const askedFor = useRef('')
  useEffect(() => {
    if (!canMoney || !jobs.data || !fresh.data) return
    const ids = amountIds(jobs.data, fresh.data)
    const key = JSON.stringify(ids)
    if (!Object.keys(ids).length || askedFor.current === key) return
    askedFor.current = key
    api.stripe_prices({ action: 'amounts', ...ids }).then(setAmounts, () => setAmounts(null))
  }, [api, canMoney, jobs.data, fresh.data])

  // Whether each recording to paste goes in the bell once it's switched on (hub.bell_will_ring, Adam,
  // 5 Oct, stage 6's batch), asked once per recording; until it answers, its card claims no bell
  const [bells, setBells] = useState({})
  const bellsAsked = useRef(new Set())
  useEffect(() => {
    if (!jobs.data) return
    const asks = bellAsks(jobs.data).filter(a => !bellsAsked.current.has(a.key))
    if (!asks.length) return
    for (const a of asks) bellsAsked.current.add(a.key)
    Promise.all(asks.map(a => api.bell_will_ring({ kind: 'new_recording', product: a.product, item: a.item }).then(rings => [a.key, rings])))
      .then(answers => setBells(b => ({ ...b, ...Object.fromEntries(answers) })))
  }, [api, jobs.data])

  // The referral cards' lines (decision 55, migration 79): hub.staff_introductions for every introduction the
  // jobs name, read once the jobs are in and again after each reload (owner and admin only). Until it answers,
  // the cards show without the names and the amount.
  const [introductions, setIntroductions] = useState(null)
  useEffect(() => {
    if (!canMoney || !jobs.data) return undefined
    const ids = introductionIds(jobs.data)
    if (!ids.length) return undefined
    let alive = true
    api.staff_introductions({ ids }).then(
      rows => { if (alive) setIntroductions(new Map((rows || []).map(r => [r.id, r]))) },
      () => {},
    )
    return () => { alive = false }
  }, [api, canMoney, jobs.data])

  // The line under a job once its button has done its work, by job
  const [msgs, setMsgs] = useState({})
  const say = useCallback((key, text, tone = 'done') => setMsgs(m => ({ ...m, [key]: { text, tone } })), [])

  // the lines a refund's own card names, so their owed cards say Refunded in place of Mark paid
  const refunds = useMemo(() => refundedLines(jobs.data || []), [jobs.data])

  const ops = useMemo(() => makeOps(api, { nowMs }), [api, nowMs])
  const ctx = useMemo(() => ({ role, nowMs, amounts, products, api, bells, introductions, refunds }), [role, nowMs, amounts, products, api, bells, introductions, refunds])

  async function onOp(op, job, card, args) {
    touched.current = true
    try {
      const line = await ops[op](job, card, args || {})
      say(job.job_key, line)
      jobs.reload()
      return true
    } catch (e) {
      // a referral's refusal in its own words (src/admin/referrals/words.js)
      say(job.job_key, op.startsWith('intro_') ? refusal(e) : api.words(e), 'problem')
      return false
    }
  }

  // Ticking a job off or back on: every flag in it, shown at once, then read again for who and when. A
  // register job its first mark closed, open again until everyone's marked, loses the database's "Register
  // taken" as it's ticked, so it stays ticked.
  async function onTick(job, done) {
    touched.current = true
    const was = job
    const now = { status: done ? 'done' : 'open', done_at: done ? new Date().toISOString() : null, done_by_name: done ? me?.person?.full_name || null : null, resolution: null }
    pending.current.set(job.job_key, now)
    setRows(list => list.map(j => (j.job_key === job.job_key ? { ...j, ...now } : j)))
    try {
      await api.tick_job(job.flag_ids, done ? (job.mm_reopened ? { resolution: null } : {}) : { done: false, resolution: null })
      pending.current.delete(job.job_key)
      setMsgs(m => { const n = { ...m }; delete n[job.job_key]; return n })
      jobs.reload()
    } catch (e) {
      pending.current.delete(job.job_key)
      setRows(list => list.map(j => (j.job_key === was.job_key ? was : j)))
      say(job.job_key, api.words(e), 'problem')
    }
  }

  const live = (schedule.data || []).filter(s => s.is_today)

  return (
    <div className="td-page">
      <PageHeader eyebrow={eyebrowFor(nowMs)} title="Today" aside={rows ? leftLine(rows) : undefined} />
      <div className="td-grid">
        <div className="td-col">
          <LiveToday items={live} nowMs={nowMs} products={products} mm={mm} />
          <NeedsYou rows={rows} status={jobs.status} error={jobs.error} reload={jobs.reload} msgs={msgs} ctx={ctx}
            onTick={onTick} onOp={onOp} />
          <NewThisWeek load={fresh} amounts={amounts} products={products} nowMs={nowMs} role={role} />
        </div>
        <div className="td-col">
          <Announcements load={news} setList={news.setData} api={api} role={role} personId={personId} products={products} nowMs={nowMs} />
          <ComingUp load={schedule} nowMs={nowMs} />
          <GoingQuiet load={quiet} nowMs={nowMs} products={products} role={role} />
          <AccessEnding load={ending} nowMs={nowMs} products={products} role={role} />
        </div>
      </div>
    </div>
  )
}
